import { useCallback } from 'react';
import {
  Channel,
  deviceUtils,
  Languages,
  useAppBridge,
  useAppContext,
  useChannelContext,
  useCurrentMenu,
  useDeviceBridge,
  useLocale,
  useWebviewBridge,
} from 'scbk-services';
import { useDevice } from 'scbk-ui-web';
import { useUtlFncCfbRegisterComfyBank } from './api/utilitiesFunctionsComfyBank';
import { Dialog, usePageMove } from 'scbk-shared-module';

export type UseChangeComfyBankProps = {
  seniorMode: 'Y' | 'N';
  dbChkYN?: 'Y' | 'N';
  callback?: () => void | Promise<void>;
  doNativeYN?: 'Y' | 'N';
};

const useChangeMode = () => {
  // api hook
  // 편한뱅킹 적재 API
  const { request: utlFncCfbRegisterComfyBank } = useUtlFncCfbRegisterComfyBank({
    onError: () => {},
  });

  // 현재 메뉴 Id
  const { currentMenuId, currentMenuInfo } = useCurrentMenu();

  // locale
  const { getLocale, setLocale } = useLocale();

  // 현재 모드
  const {
    seniorMode: currentSeniorMode,
    darkMode: currentDarkMode,
    isChangedSeniorMode,
    updateContext: updateAppContext,
  } = useAppContext();

  // 네이티브 기능
  const { doNGetDeviceStorage, doNSetDeviceStorage, doNSetComfyBanking, doNSetLocale } =
    useAppBridge();

  const { doNUpdateNavBar } = useWebviewBridge();

  const { doNGetDeviceTheme } = useDeviceBridge();

  const { setThemeMode, toggleLargeText } = useDevice();

  const { channel } = useChannelContext();

  const { goMain } = usePageMove();

  /**
   * 편한뱅킹 적재
   */
  const registerComfyBank = useCallback(
    async ({ seniorMode, dbChkYN, callback, doNativeYN = 'Y' }: UseChangeComfyBankProps) => {
      if (!currentMenuId) {
        return;
      }

      await utlFncCfbRegisterComfyBank({
        seniorMode,
        dbChkYN: dbChkYN ?? 'N',
        menuId: currentMenuId,
      });

      if (doNativeYN === 'Y' && isChangedSeniorMode !== 'Y') {
        // APP일 경우 storage set
        // native에서 편한뱅킹 설정 여부값이 필요하여 doNSetComfyBanking 호출
        if (deviceUtils.isApp()) {
          await doNSetDeviceStorage('CMM', 'SeniorMode', seniorMode);
          await doNSetComfyBanking(seniorMode);
        }

        updateAppContext({ seniorMode: seniorMode, isChangedSeniorMode: 'N' });
        toggleLargeText(seniorMode === 'Y');
      }

      setTimeout(() => {
        callback?.();
      }, 300);
    },
    [currentMenuId, isChangedSeniorMode],
  );

  /**
   * 시니어 모드 변경
   * @param seniorMode : 편한뱅킹 모드 'Y' | 'N'
   * @param dbChkYN : db적재 여부
   * @param callback
   */
  const changeSeniorMode = useCallback(
    async ({ seniorMode, dbChkYN = 'N', callback }: UseChangeComfyBankProps) => {
      registerComfyBank({ seniorMode, dbChkYN, callback });
    },
    [registerComfyBank],
  );

  /**
   * 다크 모드 변경
   */
  const changeDarkMode = useCallback(
    async (darkMode: 'Y' | 'N' | '', callback?: () => Promise<void> | void) => {
      if (deviceUtils.isApp()) {
        await doNSetDeviceStorage('CMM', 'DarkMode', darkMode);
        // 다크모드 native 감지용
        await doNUpdateNavBar();
      }
      let darkModeYN = darkMode;
      if (darkMode === '') {
        const result = await doNGetDeviceTheme();
        darkModeYN = result === 'D' ? 'Y' : 'N';
      }

      const changeMode = darkModeYN || 'N';

      await updateAppContext({ darkMode: changeMode });
      await setThemeMode(changeMode === 'Y' ? 'dark' : 'light');

      setTimeout(() => {
        callback?.();
      }, 300);
    },
    [doNSetDeviceStorage, updateAppContext, setThemeMode],
  );

  /**
   * 언어 변경 및 시니어, 다크모드 변경
   */
  const changeLocale = useCallback(async () => {
    if (getLocale() === Languages.KO) {
      setLocale(Languages.EN);
      if (deviceUtils.isApp()) {
        doNSetLocale(Languages.EN);
      } else {
        window.localStorage.setItem('locale', Languages.EN);
      }

      if (deviceUtils.isApp()) {
        try {
          // 영문 전환 시 : ASIS 시니어 모드 저장
          await doNSetDeviceStorage('CMM', 'ASISSeniorMode', currentSeniorMode);

          changeSeniorMode({ seniorMode: 'Y', dbChkYN: 'Y', callback: () => {} });
        } catch (e) {
          changeSeniorMode({ seniorMode: 'Y', dbChkYN: 'Y', callback: () => {} });
        }
      }

      // 인뱅이고 현재메뉴가 영문 미제공일때
      if (channel === Channel.IB && currentMenuInfo?.fields?.useEn !== 'Y') {
        const isOk = await Dialog.alert({ content: 'This transaction is not support English.' });

        if (isOk) {
          goMain({ type: 'HOME' });
        }
      }
    } else {
      setLocale(Languages.KO);
      // 언어 설정 스토리지 저장
      if (deviceUtils.isApp()) {
        doNSetLocale(Languages.KO);
      } else {
        window.localStorage.setItem('locale', Languages.KO);
      }

      if (deviceUtils.isApp()) {
        try {
          // 한글 전환 시 : 다크모드 => 화이트 모드 강제 set
          await changeDarkMode('N');

          // 한글 전환 시 : ASIS시니어 모드로 시니어 모드 변경
          const isSeniorModeVal = await doNGetDeviceStorage('CMM', 'ASISSeniorMode');
          console.log('### asis seniorMode : ', isSeniorModeVal);
          if (isSeniorModeVal === 'Y') {
            changeSeniorMode({ seniorMode: isSeniorModeVal, dbChkYN: 'Y', callback: () => {} });
          } else {
            changeSeniorMode({ seniorMode: 'N', dbChkYN: 'Y', callback: () => {} });
          }
        } catch (e) {
          changeSeniorMode({ seniorMode: 'N', dbChkYN: 'Y', callback: () => {} });
        }
      }
    }
  }, [
    currentSeniorMode,
    updateAppContext,
    doNSetDeviceStorage,
    doNSetDeviceStorage,
    registerComfyBank,
    setLocale,
    getLocale,
    toggleLargeText,
    currentMenuInfo,
  ]);

  return {
    seniorMode: currentSeniorMode,
    darkMode: currentDarkMode,
    changeLocale,
    changeSeniorMode,
    changeDarkMode,
    registerComfyBank,
  };
};

export { useChangeMode };
